import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../utils/Utils.js';
import { formatDateTime } from '../../utils/dateFormat.js';
import DateInput from '../shared/inputs/DateInput.js';
import Patch from '../utils/Patch.js';
import EditableText from '../shared/EditableText.js';
import { StatCard } from '../shared/lots/LotDetail.js';
import { useCurrencySymbol } from '../finances/currency.js';
import '../herd/ListHerd.css';
import '../weight/lots/WeightLots.css';
import './ListItems.css';
import { formatListCode, formatListTitle } from './listCode';
import ListProjectLink from './ListProjectLink';
import DetailWhatsAppTip from '../shared/DetailWhatsAppTip.js';
import { listTips } from '../shared/whatsappTips.js';

const STATUS_LABEL_KEY = {
  purchased: 'lists_status_purchased',
  completed: 'lists_status_completed',
};

const StatusBadge = ({ status, t }) => (
  <span className={`item-status-badge ${status}`}>
    {t(STATUS_LABEL_KEY[status] || 'lists_status_pending')}
  </span>
);

// Purchased and completed (done, no money involved) both count as done.
const isDone = (item) => item.status === 'purchased' || item.status === 'completed';

const formatCost = (cost) =>
  cost != null ? Number(cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

// ListItems — a list's detail page (/lists/all/:listId). Same shell as the
// lot and project detail pages (Trello 70qmkpWY): code, editable name and
// WhatsApp aliases, stat tiles, the linked project, then the items as cards.
const ListItems = ({ list, onArchived, onBack, onListChange, actions }) => {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const symbol   = useCurrencySymbol();

  const [allItems,       setAllItems]       = useState([]);
  const [categories,     setCategories]     = useState([]);
  const [statusFilter,   setStatusFilter]   = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [isMobile,       setIsMobile]       = useState(window.innerWidth < 768);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [editingExpiry, setEditingExpiry]   = useState(false);
  const [expiryValue,   setExpiryValue]     = useState(
    list.expires_at ? new Date(list.expires_at).toISOString().slice(0, 16) : ''
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Fetch categories for filter dropdown ─────────────────────────────────
  useEffect(() => {
    Fetch(
      '/api/data/lists.categories',
      'GET', null,
      (data) => setCategories(data || []),
      () => {}
    );
  }, []);

  // ── Fetch items ──────────────────────────────────────────────────────────
  // All of the list's items: the tiles count them all; filters apply below.
  const fetchItems = useCallback(() => {
    Fetch(`/api/data/lists.items?search_fields=list_id&search_values=${list._id}`, 'GET', null,
      (data) => setAllItems(data || []), () => {});
  }, [list._id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const items = allItems.filter(i =>
    (!statusFilter || (i.status || 'pending') === statusFilter) &&
    (!categoryFilter || i.category === categoryFilter));

  const doneCount = allItems.filter(isDone).length;
  const spent = allItems
    .filter(i => i.status === 'purchased')
    .reduce((sum, i) => sum + Number(i.cost || 0), 0);

  const activeFilterCount = [statusFilter, categoryFilter].filter(Boolean).length;

  const resetFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
  };

  // ── Archive / Unarchive ──────────────────────────────────────────────────
  const handleArchive = () => {
    const newStatus = list.status === 'archived' ? 'active' : 'archived';
    Fetch(
      '/api/data/lists.lists', 'PATCH',
      JSON.stringify({ _id: list._id, status: newStatus, ...(newStatus === 'archived' && { is_default: false }) }),
      () => {
        setConfirmArchive(false);
        if (newStatus === 'active') { onArchived(); return; }
        if (list.is_default) {
          Fetch(
            `/api/data/lists.lists?search_fields=type&search_values=${list.type}`,
            'GET', null,
            (data) => {
              const other = (data || []).find(l => l._id !== list._id && l.status !== 'archived');
              if (other) {
                Fetch(
                  '/api/data/lists.lists', 'PATCH',
                  JSON.stringify({ _id: other._id, is_default: true }),
                  onArchived, onArchived
                );
              } else {
                onArchived();
              }
            },
            onArchived
          );
        } else {
          onArchived();
        }
      },
      () => setConfirmArchive(false)
    );
  };

  // ── Print ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    Fetch(
      `/api/data/lists.items?search_fields=list_id&search_values=${list._id}`,
      'GET', null,
      (allItems) => {
        const sorted = [...(allItems || [])].sort((a, b) => {
          const ca = (a.category || '').toLowerCase();
          const cb = (b.category || '').toLowerCase();
          return ca < cb ? -1 : ca > cb ? 1 : 0;
        });
        const listTitle = formatListTitle(list, t);
        const rows = sorted.map(item => `
          <tr class="${isDone(item) ? 'purchased' : ''}">
            <td>${item.category || '—'}</td>
            <td>${item.text || ''}</td>
            <td style="text-align:center">${isDone(item) ? '✓' : ''}</td>
            <td style="color:#888;font-size:12px">${item.created_by || ''}</td>
          </tr>`).join('');
        const html = `<!DOCTYPE html><html><head><title>${listTitle}</title><style>
          body{font-family:Arial,sans-serif;padding:24px;color:#222}
          h1{font-size:18px;margin-bottom:20px}
          table{width:100%;border-collapse:collapse;font-size:14px}
          th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-weight:600}
          td{padding:8px 10px;border-bottom:1px solid #eee}
          tr.purchased td{color:#aaa;text-decoration:line-through}
          @media print{body{padding:0}}
        </style></head><body>
          <h1>${listTitle}</h1>
          <table>
            <thead><tr>
              <th>${t('category')}</th><th>${t('lists_item')}</th>
              <th>✓</th><th>${t('lists_added_by')}</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </body></html>`;
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); win.print(); }
      },
      () => {}
    );
  };

  // ── Expiry date save ─────────────────────────────────────────────────────
  const handleSaveExpiry = () => {
    const expires_at = expiryValue ? new Date(expiryValue).toISOString() : null;
    Fetch('/api/data/lists.lists', 'PATCH', JSON.stringify({ _id: list._id, expires_at }),
      () => { setEditingExpiry(false); onListChange?.({ ...list, expires_at }); },
      () => setEditingExpiry(false)
    );
  };

  // ── Name / WhatsApp aliases ──────────────────────────────────────────────
  const saveField = (field, value) =>
    Patch('lists.lists', list._id, field, value, () => onListChange?.({ ...list, [field]: value }), () => {});

  const saveAliases = (value) =>
    saveField('matches', value.split(';').map(a => a.trim()).filter(Boolean).join(';'));

  const archived = list.status === 'archived';

  // ── Filter controls (shared between desktop panel and mobile sheet) ──────
  const FilterControls = () => (
    <>
      <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
        <option value="">{t('lists_filter_all')}</option>
        <option value="pending">{t('lists_status_pending')}</option>
        <option value="purchased">{t('lists_status_purchased')}</option>
        <option value="completed">{t('lists_status_completed')}</option>
      </select>
      <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
        <option value="">{t('lists_no_category')}</option>
        {categories.map(c => (
          <option key={c._id || c.name} value={c.name}>{c.name}</option>
        ))}
      </select>
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="wld-page list-detail">
      {/* ── Breadcrumb ── */}
      {onBack && (
        <button className="list-items-back" onClick={onBack}>
          ← {t('lists_all_lists')}
        </button>
      )}

      {/* ── Header: code, name, WhatsApp aliases (like lot / project pages) ── */}
      <div className="wld-header">
        <div className="wld-header-top list-detail-top">
          <div className="wl-detail-code">{formatListCode(list, t)}</div>
          {list.is_default && <span className="default-badge">★ {t('default')}</span>}
          {archived && <span className="lists-archived-badge">{t('lists_filter_archived')}</span>}
          <button className="print-button list-detail-print" onClick={handlePrint}>
            {t('lists_print')}
          </button>
        </div>
        <EditableText value={list.name} className="wld-name" inputClassName="wld-name-input"
          placeholder={t('lists_name_placeholder')} onSave={(name) => saveField('name', name)} />
        <EditableText value={(list.matches || '').split(';').filter(Boolean).join('; ')}
          className="wld-matches" inputClassName="wld-matches-input"
          label={t('nicknames')} placeholder={t('lists_aliases_placeholder')} onSave={saveAliases} />
      </div>

      {!archived && (
        <DetailWhatsAppTip title={t('list_tip_title')} tips={listTips(list, formatListCode(list, t), t)} />
      )}

      {/* ── Tiles ── */}
      <div className="wl-stats-grid">
        <StatCard label={t('lists_item_count')} value={allItems.length} />
        <StatCard label={t('lists_done')} value={t('lists_done_of', { done: doneCount, total: allItems.length })} />
        <StatCard label={t('lists_spent')} value={`${symbol}${spent.toFixed(2)}`} />
        {!editingExpiry ? (
          <StatCard
            label={t('lists_expires_at')}
            value={list.expires_at
              ? <span className="list-tile-date">{formatDateTime(list.expires_at, { dateStyle: 'short', timeStyle: 'short' })}</span>
              : <span className="list-tile-none">{t('lists_no_expiry')}</span>}
            onClick={archived ? undefined : () => setEditingExpiry(true)} />
        ) : (
          <div className="wl-stat-card list-tile-edit">
            <div className="wl-stat-label">{t('lists_expires_at')}</div>
            <DateInput showTime value={expiryValue} onChange={(v) => setExpiryValue(v)}
              required={false} defaultToToday={false} autoFocus />
            <div className="list-tile-edit-buttons">
              <button className="list-items-expiry-save" onClick={handleSaveExpiry}>{t('save')}</button>
              <button className="list-items-expiry-cancel" onClick={() => setEditingExpiry(false)}>{t('cancel')}</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Linked finance project ── */}
      <ListProjectLink list={list} onChange={onListChange} />

      <div className="list-section-header">
        <h3>{t('lists_items')}</h3>
      </div>

      {/* ── Filters ── */}
      {isMobile ? (
        <div className="mobile-search-filter">
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            {t('filters')}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="desktop-filter-bar">
          <div className="desktop-filter-top">
            <button
              className={`desktop-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
              onClick={() => setFiltersOpen(o => !o)}
            >
              {t('filters')}
              {activeFilterCount > 0 && <span className="desktop-filter-badge">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && (
              <button className="desktop-filter-reset" onClick={resetFilters}>
                {t('reset_filters')}
              </button>
            )}
            {actions && <div className="desktop-filter-actions">{actions}</div>}
          </div>
          {filtersOpen && (
            <div className="desktop-filter-panel">
              <FilterControls />
            </div>
          )}
        </div>
      )}

      {/* ── Items ── */}
      {items.length === 0 ? (
        <div className="list-empty">{t('lists_no_items')}</div>
      ) : (
        <div className="list-item-cards">
          {items.map(item => (
            <div
              key={item._id}
              className={`list-item-card${isDone(item) ? ' purchased' : ''}`}
              onClick={() => navigate(`/lists/items/${item._id}`, { state: { item, selectedList: list } })}
            >
              <div>
                <div className="list-item-card-text">{item.text}</div>
                <div className="list-item-card-meta">
                  {item.category && <span>{item.category} · </span>}
                  {item.cost != null && <span>{symbol}{formatCost(item.cost)} · </span>}
                  {item.created_by}
                  {item.created_at && <span> · {item.created_at.substring(0, 10)}</span>}
                </div>
              </div>
              <div className="list-item-card-right">
                <StatusBadge status={item.status} t={t} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Archive / Unarchive (danger zone) ── */}
      <hr className="list-items-divider" />
      {list.status === 'archived' ? (
        <button className="list-items-unarchive" onClick={handleArchive}>
          {t('lists_unarchive')}
        </button>
      ) : !confirmArchive ? (
        <button className="list-items-archive" onClick={() => setConfirmArchive(true)}>
          {t('lists_archive')}
        </button>
      ) : (
        <button className="list-items-archive-confirm" onClick={handleArchive}>
          {t('lists_archive_confirm')}
        </button>
      )}

      {/* ── Mobile filter sheet ── */}
      {isMobile && filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t('filters')}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <FilterControls />
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={() => { resetFilters(); setFiltersOpen(false); }}
                disabled={activeFilterCount === 0}
              >
                {t('reset_filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t('done')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListItems;
